import { useCallback, useState } from "react";
import appLogger from "../../infra/logger/appLogger";
import { useAppDispatch, useAppSelector } from "../hook";
import { useTranslation } from "../localization";
import { ModerationFlows, RateLimitFlows, useImageErrors, useInputValidationErrors, useModerationErrors, useRateLimitErrors } from "../miscellaneous";
import { createCharacter, getCharacterById, reportCharacter, setCharacterData, updateCharacter } from "./characterSlice";
import { CharacterFlow } from "./types";
const characterLogger = appLogger.withTag("Character");
export default function useCharacter() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { getMessage: getRateLimitErrorMessage } = useRateLimitErrors();
    const { getMessage: getModerationErrorMessage } = useModerationErrors();
    const { getMessage: getInputValidationErrorMessage } = useInputValidationErrors();
    const { getMessage: getImageErrorMessage } = useImageErrors();
    const { loading, loadingType } = useAppSelector((state) => state.character);
    const characterData = useAppSelector((state) => state.character.characterData);
    const [characterError, setCharacterError] = useState(null);
    const resetError = () => {
        setCharacterError(null);
    };
    const resetCharacterData = () => {
        dispatch(setCharacterData(null));
    };
    const getCharacterData = useCallback(async (characterId, options) => {
        try {
            setCharacterError(null);
            const result = await dispatch(getCharacterById({
                id: characterId,
                ...options,
            })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message;
            setCharacterError({
                errorType: CharacterFlow.GET_CHARACTER,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            characterLogger.error(`Error fetching character: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch]);
    const onUserCreateCharacter = useCallback(async (characterData) => {
        try {
            setCharacterError(null);
            const result = await dispatch(createCharacter(characterData)).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorCode = apiError?.errorCode;
            const rateLimitErrorMessage = getRateLimitErrorMessage({
                errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: RateLimitFlows.CREATE_CHATBOT,
                },
            });
            const moderationErrorMessage = getModerationErrorMessage({
                errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: ModerationFlows.CREATE_CHATBOT,
                },
            });
            const imageErrorMessage = getImageErrorMessage({ errorCode, skipDefaultMessage: true });
            const errorMessage = rateLimitErrorMessage || moderationErrorMessage || imageErrorMessage || apiError?.message || t("error:fallbackError");
            setCharacterError({
                errorType: CharacterFlow.CREATE_CHARACTER,
                errorMessage: errorMessage,
                errorCode,
                errorData: apiError?.data,
            });
            characterLogger.error(`Error creating character: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getRateLimitErrorMessage, getModerationErrorMessage, getImageErrorMessage, t]);
    const onUserUpdateCharacter = useCallback(async (id, characterData) => {
        try {
            setCharacterError(null);
            const result = await dispatch(updateCharacter({ id, params: characterData })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorCode = apiError?.errorCode;
            const moderationErrorMessage = getModerationErrorMessage({
                errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: ModerationFlows.UPDATE_CHATBOT,
                },
            });
            const imageErrorMessage = getImageErrorMessage({ errorCode, skipDefaultMessage: true });
            const errorMessage = moderationErrorMessage || imageErrorMessage || apiError?.message || t("error:fallbackError");
            setCharacterError({
                errorType: CharacterFlow.UPDATE_CHARACTER,
                errorMessage: errorMessage,
                errorCode,
                errorData: apiError?.data,
            });
            characterLogger.error(`Error updating character: ${errorMessage}`, error);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getModerationErrorMessage, getImageErrorMessage, t]);
    const onUserReportCharacter = useCallback(async (reportData) => {
        try {
            setCharacterError(null);
            const result = await dispatch(reportCharacter(reportData)).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = getRateLimitErrorMessage({
                errorCode: apiError?.errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: RateLimitFlows.REPORT_CHATBOT,
                },
            }) ||
                getInputValidationErrorMessage({ errorCode: apiError?.errorCode, skipDefaultMessage: true }) ||
                apiError.message ||
                t("error:fallbackError");
            setCharacterError({
                errorType: CharacterFlow.REPORT_CHARACTER,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            characterLogger.error(`Error reporting character: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getRateLimitErrorMessage, getInputValidationErrorMessage, t]);
    return {
        characterData,
        isLoading: loading,
        loadingType,
        errorType: characterError?.errorType || null,
        errorMessage: characterError?.errorMessage || null,
        errorCode: characterError?.errorCode || null,
        errorData: characterError?.errorData || null,
        getCharacterData,
        onUserCreateCharacter,
        onUserUpdateCharacter,
        onUserReportCharacter,
        resetError,
        resetCharacterData,
    };
}
